import { Injectable } from '@angular/core';
import { getDefaultClientId, getUserId } from '@api-ui-app/src/app/ngrx/auth.reducer';
import { getCompanyDetails, getState } from '@api-ui-app/src/app/ngrx/company-details.reducer';
import { catchError, distinctUntilChanged, filter, first, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '@api-ui-app/src/app/app.state';
import { KeycloakAuthService } from '@api-ui-app/src/app/shared/services/keycloak-auth.service';
import * as companyDetailsActions from '@api-ui-app/src/app/ngrx/company-details.actions';
import { CompanyDetails } from '@gelato-api-ui/core/company-details/company-details';
import {
  quickQuestionConfig,
  QuickQuestions,
  QuickQuestionsSteps,
} from '@gelato-api-ui/core/quick-questions/types/quick-questions';
import { QuickQuestionsService } from '@gelato-api-ui/core/quick-questions/services/quick-questions.service';
import { QuestionnaireApiService } from '@gelato-api-ui/core/quick-questions/services/questionnaire-api.service';
import { ErrorData } from '@api-ui-app/src/app/shared/lib/error-data';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { Question } from '@gelato-api-ui/core/quick-questions/types/question';
import { addNewUserParameterToShopifyLinkUrl } from '@api-ui-app/src/app/e-commerce-stores/lib/helpers/addNewUserParameterToShopifyLinkUrl';
import { LastRequestedUrlService } from '@api-ui-app/src/app/shared/services/last-requested-url.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { getFormFieldError } from '@api-ui-app/src/app/lib/getFormFieldError';
import { logEvent } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { deviceName } from '@api-ui-app/src/app/lib/mobileDeviceDetected';
import * as R from 'ramda';
import { QuickQuestionButtonsConfig } from './types/quick-question-buttons-config';
import { QuickQuestionCommonOptionsEnum } from '@gelato-api-ui/core/quick-questions/types/quick-question-common-options.enum';
import { CountryListWithDialingCodesService } from '@gelato-api-ui/sdk/src/lib/shared/country-selector/country-list-with-dialing-codes.service';

@Injectable()
export class OnBoardingFacade {
  defaultClientId$ = this.store.select(getDefaultClientId);
  userId$ = this.store.select(getUserId);
  companyDetails$ = this.store.select(getCompanyDetails);
  countries$ = this.countryListWithDialingCodesService.getCountriesWithDialingCodes();
  isTermsAccepted$ = this.auth.isTermsAccepted();
  isLoading$ = this.store.select(getState).pipe(map(_state => _state.isLoading));

  originalQuickQuestions$ = this.companyDetails$.pipe(
    map(
      (companyDetails: CompanyDetails): QuickQuestions =>
        companyDetails && companyDetails.questionnaire
          ? this.quickQuestionsService.fromQuestionnaireArray(companyDetails.questionnaire)
          : new QuickQuestions(),
    ),
  );

  isSubmittingForm$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<ErrorData>(null);
  activeQuickQuestionStep$ = new BehaviorSubject<QuickQuestionsSteps>(null);
  availableQuickQuestionSteps$ = new BehaviorSubject<QuickQuestionsSteps[]>([]);

  private _quickQuestions$ = new BehaviorSubject<QuickQuestions>(null);

  quickQuestions$ = combineLatest([this._quickQuestions$, this.isTermsAccepted$]).pipe(
    map(([quickQuestions, isTermsAccepted]) => ({
      ...(quickQuestions || new QuickQuestions()),
      termsAndConditions: (quickQuestions ? quickQuestions.termsAndConditions : false) || isTermsAccepted,
    })),
  );

  activeQuickQuestionStepIndex$ = combineLatest([
    this.availableQuickQuestionSteps$,
    this.activeQuickQuestionStep$,
  ]).pipe(
    map(([availableQuickQuestionSteps, activeQuickQuestionStep]): number =>
      availableQuickQuestionSteps.findIndex(
        (loopQuickQuestionStep: QuickQuestionsSteps): boolean => activeQuickQuestionStep === loopQuickQuestionStep,
      ),
    ),
  );

  previousQuickQuestionStep$ = combineLatest([
    this.availableQuickQuestionSteps$,
    this.activeQuickQuestionStepIndex$,
  ]).pipe(
    map(
      ([availableQuickQuestionSteps, activeQuickQuestionStepIndex]) =>
        availableQuickQuestionSteps[activeQuickQuestionStepIndex - 1],
    ),
  );

  nextQuickQuestionStep$ = combineLatest([this.availableQuickQuestionSteps$, this.activeQuickQuestionStepIndex$]).pipe(
    map(
      ([availableQuickQuestionSteps, activeQuickQuestionStepIndex]) =>
        availableQuickQuestionSteps[activeQuickQuestionStepIndex + 1],
    ),
  );

  isFirstQuickQuestionStep$ = this.previousQuickQuestionStep$.pipe(
    map((previousQuickQuestion: QuickQuestionsSteps): boolean => !previousQuickQuestion),
  );

  isLastQuickQuestionStep$ = this.nextQuickQuestionStep$.pipe(
    map((nextQuickQuestion: QuickQuestionsSteps): boolean => !nextQuickQuestion),
  );

  canProceed$ = combineLatest([this.quickQuestions$, this.activeQuickQuestionStep$]).pipe(
    map(
      ([quickQuestions, activeQuickQuestionStep]): boolean =>
        !this.quickQuestionsService.validate(quickQuestions, activeQuickQuestionStep, false, true),
    ),
  );
  canProceedToFinish$ = combineLatest([this.isLastQuickQuestionStep$, this.canProceed$]).pipe(
    map(([isLastQuickQuestionStep, canProceed]) => isLastQuickQuestionStep && canProceed),
  );

  configForActiveStep$ = this.activeQuickQuestionStep$.pipe(map(activeStep => quickQuestionConfig[activeStep]));

  showNextBtn$ = this.configForActiveStep$.pipe(map(config => config?.custom || config?.multiple));

  buttonsConfig$: Observable<QuickQuestionButtonsConfig> = combineLatest([
    this.quickQuestions$,
    this.activeQuickQuestionStep$,
    this.showNextBtn$,
  ]).pipe(map(this.mapToButtonsConfig), distinctUntilChanged(R.equals));

  automaticallyGoToNextStep$ = combineLatest([this.buttonsConfig$, this.isLastQuickQuestionStep$]).pipe(
    map(
      ([buttonsConfig, isLastQuickQuestionStep]) =>
        !buttonsConfig?.showSkipBtn && !buttonsConfig?.showNextBtn && !isLastQuickQuestionStep,
    ),
  );

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly store: Store<AppState>,
    private readonly auth: KeycloakAuthService,
    private readonly quickQuestionsService: QuickQuestionsService,
    private readonly questionnaireApiService: QuestionnaireApiService,
    private readonly countryListWithDialingCodesService: CountryListWithDialingCodesService,
    private readonly lastRequestedUrlService: LastRequestedUrlService,
  ) {}

  mapToButtonsConfig([quickQuestions, activeQuickQuestionStep, showNextBtn]: [
    QuickQuestions,
    QuickQuestionsSteps,
    boolean,
  ]): QuickQuestionButtonsConfig {
    const defaultValues: QuickQuestionButtonsConfig = {
      showSkipBtn: false,
      showNextBtn,
    };
    const currentAnswer = quickQuestions[activeQuickQuestionStep];
    const isPhonePrefixFilled = !!quickQuestions[QuickQuestionsSteps.phonePrefix];
    const conditions = {
      [QuickQuestionsSteps.ecommercePlatform]: () => ({
        showSkipBtn: !currentAnswer || (currentAnswer as string[])?.length === 0,
        showNextBtn: !!(currentAnswer as string[])?.length,
      }),
      [QuickQuestionsSteps.storeLink]: () => ({
        showSkipBtn: !currentAnswer,
        showNextBtn: !!currentAnswer,
      }),
      [QuickQuestionsSteps.onlinePresence]: () => ({
        showNextBtn: currentAnswer === QuickQuestionCommonOptionsEnum.Other,
      }),
      [QuickQuestionsSteps.superpower]: () => ({
        showNextBtn: currentAnswer === QuickQuestionCommonOptionsEnum.Other,
      }),
      [QuickQuestionsSteps.phoneNumber]: () => ({
        showSkipBtn: !isPhonePrefixFilled || !currentAnswer,
        showNextBtn: isPhonePrefixFilled && !!currentAnswer,
      }),
    };

    return {
      ...defaultValues,
      ...(conditions[activeQuickQuestionStep] && conditions[activeQuickQuestionStep]()),
    };
  }

  hasError(fieldName: string): Observable<boolean> {
    return this.error$.pipe(map((error: ErrorData) => Boolean(getFormFieldError(error, fieldName))));
  }

  init() {
    this.isSubmittingForm$.next(false);
    this.error$.next(null);
    this.setQuickQuestions(new QuickQuestions());

    this.loadCompanyDetails();
    this.goToFirstQuickQuestion();
    this.logStartEvent();
  }

  setQuickQuestions(quickQuestions: QuickQuestions) {
    this._quickQuestions$.next(quickQuestions);
    this.updateSteps(quickQuestions);
  }

  setTermsAndConditions(termsAndConditions: boolean) {
    this.quickQuestions$
      .pipe(
        first(),
        map(
          (quickQuestions: QuickQuestions): QuickQuestions => ({
            ...quickQuestions,
            termsAndConditions,
          }),
        ),
        tap((quickQuestions: QuickQuestions) => {
          this.setQuickQuestions(quickQuestions);
        }),
      )
      .subscribe();
  }

  setReceiveNews(receiveNews: boolean) {
    this.quickQuestions$
      .pipe(
        first(),
        map(
          (quickQuestions: QuickQuestions): QuickQuestions => ({
            ...quickQuestions,
            receiveNews,
          }),
        ),
        tap((quickQuestions: QuickQuestions) => {
          this.setQuickQuestions(quickQuestions);
        }),
      )
      .subscribe();
  }

  goToFirstQuickQuestion() {
    this.activeQuickQuestionStep$.next(QuickQuestionsSteps.sellingProductsToday);
  }

  goToPreviousQuickQuestion() {
    this.previousQuickQuestionStep$
      .pipe(
        first(),
        tap((previousQuickQuestion: QuickQuestionsSteps) => {
          this.error$.next(null);
          this.activeQuickQuestionStep$.next(previousQuickQuestion);
        }),
      )
      .subscribe();
  }

  goToNextQuickQuestion(answer?: string) {
    this.nextQuickQuestionStep$
      .pipe(
        withLatestFrom(this.quickQuestions$),
        first(),
        switchMap(([nextQuickQuestion, quickQuestions]) =>
          this.validateQuickQuestions().pipe(
            tap((error: ErrorData) => {
              this.error$.next(error);

              if (!error) {
                const question = this.activeQuickQuestionStep$.value;
                const signupAnswer = answer ?? quickQuestions[question]?.toString();
                this.logQuestionEvent(question, signupAnswer);
                this.activeQuickQuestionStep$.next(nextQuickQuestion);
              }
            }),
          ),
        ),
      )
      .subscribe();
  }

  submitQuickQuestions() {
    this.isSubmittingForm$.next(true);
    this.validateQuickQuestions()
      .pipe(
        tap((quickQuestionsValidationError: ErrorData) => {
          this.error$.next(quickQuestionsValidationError);

          if (quickQuestionsValidationError) {
            this.isSubmittingForm$.next(false);
            return;
          }

          this.quickQuestions$
            .pipe(
              first(),
              withLatestFrom(this.route.queryParams),
              switchMap(([quickQuestions, queryParams]) => {
                return this.activateUser().pipe(
                  switchMap(() =>
                    this.saveQuickQuestions(quickQuestions).pipe(
                      tap((response: CompanyDetails) => {
                        if (response) {
                          this.processSuccessfulQuickQuestionsSavingResponse(response, queryParams);
                        } else {
                          this.isSubmittingForm$.next(false);
                        }
                      }),
                      catchError(error => {
                        this.error$.next(error.response.error);

                        return of(null);
                      }),
                    ),
                  ),
                );
              }),
            )
            .subscribe();
        }),
      )
      .subscribe();
  }

  private updateSteps(quickQuestions: QuickQuestions) {
    const steps = this.quickQuestionsService.getAvailableSteps(quickQuestions);

    this.availableQuickQuestionSteps$.next(steps);
  }

  private loadCompanyDetails() {
    this.store.dispatch(new companyDetailsActions.Load());
  }

  private validateQuickQuestions(): Observable<ErrorData> {
    return this.quickQuestions$.pipe(
      first(),
      withLatestFrom(this.activeQuickQuestionStep$, this.isLastQuickQuestionStep$),
      map(
        ([quickQuestions, activeQuickQuestionStep, isLastQuickQuestionStep]): ErrorData =>
          this.quickQuestionsService.validate(quickQuestions, activeQuickQuestionStep, isLastQuickQuestionStep),
      ),
    );
  }

  private activateUser(): Observable<any> {
    return this.auth.activateUser().pipe(
      switchMap(() =>
        this.companyDetails$.pipe(
          filter((details: CompanyDetails): boolean => details && !!details.clientId),
          first(),
        ),
      ),
    );
  }

  private saveQuickQuestions(quickQuestions: QuickQuestions): Observable<CompanyDetails> {
    return combineLatest([this.companyDetails$, this.defaultClientId$]).pipe(
      first(),
      switchMap(([companyDetails, defaultClientId]) => {
        const questionnaire: Question[] = this.quickQuestionsService.toQuestionnaireArray(quickQuestions);

        return this.questionnaireApiService.save(defaultClientId, { questionnaire }).pipe(
          first(),
          tap(() => this.store.dispatch(new companyDetailsActions.SetQuestionnaire(questionnaire))),
          map(() => ({
            ...companyDetails,
            questionnaire,
          })),
        );
      }),
    );
  }

  private logQuestionEvent(signupQuestion: string, signupAnswer: string) {
    logEvent('SignupQuestions', {
      signupQuestion,
      signupAnswer,
      deviceType: deviceName(),
    });
  }
  private logStartEvent() {
    this.isTermsAccepted$
      .pipe(
        first(),
        tap(isTermsAccepted =>
          logEvent('SignupQuestionsStart', {
            registrationMethod: isTermsAccepted ? 'email' : 'socialNetwork',
            deviceType: deviceName(),
          }),
        ),
      )
      .subscribe();
  }

  private processSuccessfulQuickQuestionsSavingResponse(companyDetails: CompanyDetails, queryParams: Params) {
    const quickQuestions = this.quickQuestionsService.fromQuestionnaireArray(companyDetails.questionnaire);

    this.trackQuickQuestionnaireSubmitted(quickQuestions);
    this.auth.userRegistrationEvent(quickQuestions, companyDetails);

    const urlKeys = [null, this.lastRequestedUrlService.activationKey];

    const shopifyConnectionUrl = urlKeys.reduce((acc: string, key: string): string => {
      if (acc) {
        return acc;
      }

      if (this.lastRequestedUrlService.isShopifyConnectMode(key)) {
        const url = this.lastRequestedUrlService.get(key);

        return addNewUserParameterToShopifyLinkUrl(url);
      }

      return null;
    }, null);

    if (shopifyConnectionUrl) {
      this.router.navigateByUrl(shopifyConnectionUrl);

      urlKeys.forEach((key: string) => {
        this.lastRequestedUrlService.clear(key);
      });
    } else {
      this.router.navigate(['/home'], {
        replaceUrl: true,
        queryParams: { ...queryParams, justRegistered: true },
      });
    }
  }

  private trackQuickQuestionnaireSubmitted(quickQuestions: QuickQuestions) {
    logEvent('questionnaireSubmitted', {
      [QuickQuestionsSteps.sellingProductsToday]: quickQuestions.sellingProductsToday,
      [QuickQuestionsSteps.onlinePresence]: quickQuestions.onlinePresence,
      [QuickQuestionsSteps.customOnlinePresence]: quickQuestions.customOnlinePresence,
      [QuickQuestionsSteps.followers]: quickQuestions.followers,
      [QuickQuestionsSteps.isAlreadySelling]: quickQuestions.isAlreadySelling,
      [QuickQuestionsSteps.ecommerceStoreYearlyRevenue]: quickQuestions.ecommerceStoreYearlyRevenue,
      [QuickQuestionsSteps.industry]: quickQuestions.industry,
      [QuickQuestionsSteps.customIndustry]: quickQuestions.customIndustry,
      [QuickQuestionsSteps.ecommercePlatform]: quickQuestions.ecommercePlatform,
      [QuickQuestionsSteps.storeLink]: quickQuestions.storeLink,
      [QuickQuestionsSteps.customEcommercePlatform]: quickQuestions.customEcommercePlatform,
      [QuickQuestionsSteps.phoneNumber]: quickQuestions.phonePrefix + quickQuestions.phoneNumber,
      [QuickQuestionsSteps.superpower]: quickQuestions.superpower,
      [QuickQuestionsSteps.referer]: quickQuestions.referer,
    });
  }
}
